'use client'

import { useEffect, useState } from 'react'
import Link from 'next/link'
import LogoImage from '@/components/LogoImage'

const NAV_LINKS = [
  { label: 'Home', href: '/' },
  { label: 'Il Club', href: '/club' },
  { label: 'Squadre', href: '/squadre' },
  { label: 'Persone', href: '/persone' },
  { label: 'Calendario', href: '/calendario' },
  { label: 'News', href: '/news' },
  { label: 'Sponsor', href: '/sponsor' },
]

export default function Header() {
  const [isLive, setIsLive] = useState(false)
  // Indicatore reale: acceso solo con un operatore collegato a una gara in corso.
  // `isLive` resta la variante estetica della pagina `/live`.
  const [liveNow, setLiveNow] = useState(false)
  const [open, setOpen] = useState(false)
  const [theme, setTheme] = useState<'dark' | 'light'>('light')

  useEffect(() => {
    setIsLive(window.location.pathname === '/live')
  }, [])

  useEffect(() => {
    let alive = true
    async function aggiorna() {
      try {
        const res = await fetch('/api/live/status', { cache: 'no-store' })
        const data = (await res.json().catch(() => ({}))) as { live?: boolean }
        if (alive) setLiveNow(data.live === true)
      } catch {
        // Rete assente: si resta sullo stato noto, si riprova al giro dopo.
      }
    }
    void aggiorna()
    const timer = setInterval(() => {
      if (document.visibilityState === 'hidden') return
      void aggiorna()
    }, 20_000)
    return () => {
      alive = false
      clearInterval(timer)
    }
  }, [])

  useEffect(() => {
    const storedTheme = window.localStorage.getItem('pallavolo-andria-theme')
    const resolvedTheme = storedTheme === 'dark' ? 'dark' : 'light'
    setTheme(resolvedTheme)
  }, [])

  useEffect(() => {
    document.documentElement.dataset.theme = theme
    document.body.dataset.theme = theme
    window.localStorage.setItem('pallavolo-andria-theme', theme)
  }, [theme])

  function toggleTheme() {
    setTheme((currentTheme) => (currentTheme === 'dark' ? 'light' : 'dark'))
  }

  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 border-b transition-colors theme-header"
      style={isLive ? {
        background: 'transparent',
        borderColor: 'transparent',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
      } : undefined}
    >
      <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
        <Link href="/" className="flex items-center gap-3 shrink-0">
          <LogoImage src="/logo.png" alt="Pallavolo Andria" className="h-12 w-auto" />
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {NAV_LINKS.map((link) => (
            <Link
              key={link.href}
              href={link.href}
              className="theme-header-link px-3 py-2 text-sm font-display font-semibold uppercase tracking-wide rounded transition-colors"
            >
              {link.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={toggleTheme}
            className="theme-toggle hidden sm:inline-flex items-center justify-center h-10 w-10 border rounded-full transition-colors"
            aria-label={theme === 'dark' ? 'Attiva tema chiaro' : 'Attiva tema scuro'}
            title={theme === 'dark' ? 'Attiva tema chiaro' : 'Attiva tema scuro'}
          >
            {theme === 'dark' ? (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v2.25M12 18.75V21M4.72 4.72l1.59 1.59M17.69 17.69l1.59 1.59M3 12h2.25M18.75 12H21M4.72 19.28l1.59-1.59M17.69 6.31l1.59-1.59" />
                <circle cx="12" cy="12" r="4.25" />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 12.79A9 9 0 1111.21 3c0 .34.02.67.05 1a7 7 0 009.74 8.74c.33.03.66.05 1 .05z" />
              </svg>
            )}
          </button>

          <Link
            href="/live"
            className="hidden sm:flex items-center gap-2 bg-[#0882BD] hover:bg-blue-700 text-white font-display font-bold uppercase text-sm px-4 py-2 rounded transition-colors"
          >
            {liveNow ? (
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500" />
              </span>
            ) : (
              <span aria-hidden="true" className="inline-flex h-2 w-2 rounded-full bg-white/40" />
            )}
            LIVE
          </Link>

          <button
            className="lg:hidden p-2 theme-text-contrast"
            onClick={() => setOpen(!open)}
            aria-label="Menu"
          >
            {open ? (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </button>
        </div>
      </div>

      {open && (
        <div className="theme-header lg:hidden border-t px-4 py-3 flex flex-col gap-1">
          <button
            type="button"
            onClick={() => {
              toggleTheme()
              setOpen(false)
            }}
            className="theme-header-link px-3 py-3 text-left font-display font-semibold uppercase tracking-wide rounded transition-colors flex items-center gap-3"
          >
            {theme === 'dark' ? (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v2.25M12 18.75V21M4.72 4.72l1.59 1.59M17.69 17.69l1.59 1.59M3 12h2.25M18.75 12H21M4.72 19.28l1.59-1.59M17.69 6.31l1.59-1.59" />
                <circle cx="12" cy="12" r="4.25" />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 12.79A9 9 0 1111.21 3c0 .34.02.67.05 1a7 7 0 009.74 8.74c.33.03.66.05 1 .05z" />
              </svg>
            )}
            <span>{theme === 'dark' ? 'Tema chiaro' : 'Tema scuro'}</span>
          </button>
          {NAV_LINKS.map((link) => (
            <Link
              key={link.href}
              href={link.href}
              onClick={() => setOpen(false)}
              className="theme-header-link px-3 py-3 font-display font-semibold uppercase tracking-wide rounded transition-colors"
            >
              {link.label}
            </Link>
          ))}
          <Link
            href="/live"
            onClick={() => setOpen(false)}
            className="mt-2 flex items-center gap-2 bg-[#0882BD] text-white font-display font-bold uppercase px-4 py-3 rounded"
          >
            {liveNow ? (
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500" />
              </span>
            ) : (
              <span aria-hidden="true" className="inline-flex h-2 w-2 rounded-full bg-white/40" />
            )}
            LIVE
          </Link>
        </div>
      )}
    </header>
  )
}
